import { useEffect, useRef, type RefObject } from "react"

const FLASH_DURATION_MS = 1200
const DEFAULT_FLASH_CLASS = "value-flash"

/**
 * Returns a ref to attach to an element. When `value` changes (excluding
 * null/undefined transitions which represent loading), the hook briefly
 * applies a CSS class to drive a flash animation. State is held in the DOM,
 * not React, so the host component does not re-render on flash start/end.
 */
export function useFlashOnChange<E extends HTMLElement = HTMLElement>(
  value: unknown,
  flashClass: string = DEFAULT_FLASH_CLASS,
): RefObject<E | null> {
  const elementRef = useRef<E | null>(null)
  const prevRef = useRef<unknown>(undefined)

  useEffect(() => {
    const prev = prevRef.current
    prevRef.current = value

    if (prev === undefined || prev == null) return

    const el = elementRef.current

    if (value == null) {
      el?.classList.remove(flashClass)
      return
    }

    if (Object.is(prev, value)) return
    if (!el) return

    // Force CSS animation restart: removing the class, triggering a synchronous
    // reflow, then re-adding it makes back-to-back flashes visually replay.
    el.classList.remove(flashClass)
    void el.offsetWidth
    el.classList.add(flashClass)

    const timer = setTimeout(() => {
      elementRef.current?.classList.remove(flashClass)
    }, FLASH_DURATION_MS)

    return () => clearTimeout(timer)
  }, [value, flashClass])

  return elementRef
}
